import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Btn, Chip, Pill, Spinner } from "@/components/rp";
import { EmailLog, Section } from "@/components/commesse";
import { ItemRow, PartyDialog } from "@/components/admin/pratica";
import { ExtrasSection } from "@/components/admin/extras";

const AssigneeSelect = ({ value, staff, onChange, testid }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value || null)} className="rp-field bg-white h-9 py-0 text-sm w-auto" data-testid={testid}>
    <option value="">Nessun responsabile</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
  </select>
);

export const Decision = ({ p, patch }) => (
  <Section label="Subappalti · se serve" testid="subcontract-decision" action={<AssigneeSelect value={p.assignees.decisione} staff={p.staff} onChange={(v) => patch({ assignees: { decisione: v } })} testid="assignee-decisione" />}>
    <div className="flex flex-wrap items-center gap-2">
      <Chip active={p.subcontract_needed === true} onClick={() => patch({ subcontract_needed: true })} data-testid="subcontract-yes">Serve il subappalto</Chip>
      <Chip active={p.subcontract_needed === false} onClick={() => patch({ subcontract_needed: false })} data-testid="subcontract-no">Non serve</Chip>
      {p.subcontract_needed === null && <Pill tone="warning">Da decidere</Pill>}
      {p.subcontract_decided_by && <span className="text-xs text-[#757575]">Deciso da {p.subcontract_decided_by.name} il {fmtDateTime(p.subcontract_decided_by.at)}</span>}
    </div>
  </Section>
);

export const Parties = ({ p, kind, oid, setP, onAdd, onEdit }) => {
  const list = p.parties.filter((x) => x.kind === kind);
  const section = kind === "subappalto" ? "subappalti" : "collaboratori";
  const remove = async (x) => {
    if (!window.confirm(`Rimuovere ${x.name} e la sua checklist?`)) return;
    try { setP((await api.delete(`/amministrazione/${oid}/parties/${x.id}`)).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const patch = async (v) => { try { setP((await api.patch(`/amministrazione/${oid}`, { assignees: { [section]: v } })).data); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Section label={kind === "subappalto" ? "Subappalti" : "Collaboratori"} testid={`section-${section}`}
      action={<div className="flex gap-2"><AssigneeSelect value={p.assignees[section]} staff={p.staff} onChange={patch} testid={`assignee-${section}`} /><Btn variant="outline" className="h-9" onClick={onAdd} data-testid={`add-party-${kind}`}><Plus className="h-4 w-4" /> Aggiungi</Btn></div>}>
      {!list.length && <p className="text-sm text-[#757575]">Nessun {kind === "subappalto" ? "subappaltatore" : "collaboratore"} su questo cantiere.</p>}
      <div className="space-y-6">
        {list.map((x) => (
          <div key={x.id} data-testid={`party-${x.id}`}>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <p className="font-semibold">{x.name}</p>
              <span className="text-sm text-[#757575]">{[x.email, x.phone].filter(Boolean).join(" · ")}</span>
              <button onClick={() => onEdit(x)} className="text-xs underline text-[#757575] hover:text-black" data-testid={`party-edit-${x.id}`}>{x.name.includes("da definire") ? "Indica la ditta" : "Modifica"}</button>
              <Pill tone="sand">{x.progress.done}/{x.progress.total}</Pill>
              {list.length > 1 && <button onClick={() => remove(x)} className="ml-auto text-[#757575] hover:text-[#DC3545]" data-testid={`party-delete-${x.id}`}><Trash2 className="h-4 w-4" /></button>}
            </div>
            <div className="space-y-2">{x.items.map((it) => <ItemRow key={it.key} it={it} oid={oid} scope={x.id} staff={p.staff} onSaved={setP} />)}</div>
          </div>
        ))}
      </div>
    </Section>
  );
};

export default function AdminAmministrazioneDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [party, setParty] = useState(null);
  const [editing, setEditing] = useState(null);
  useEffect(() => { api.get(`/amministrazione/${id}`).then((r) => setP(r.data)); }, [id]);
  const patch = async (body) => { try { setP((await api.patch(`/amministrazione/${id}`, body)).data); } catch (e) { toast.error(errMsg(e)); } };
  const addParty = async (body) => { try { setP((await api.post(`/amministrazione/${id}/parties`, body)).data); setParty(null); } catch (e) { toast.error(errMsg(e)); } };
  const editParty = async (body) => { try { setP((await api.patch(`/amministrazione/${id}/parties/${editing.id}`, body)).data); setEditing(null); } catch (e) { toast.error(errMsg(e)); } };
  if (!p) return <Spinner />;
  const o = p.order;
  return (
    <div className="space-y-6" data-testid="admin-pratica-detail">
      <Link to="/admin/amministrazione" className="inline-flex items-center gap-2 text-sm text-[#757575] hover:text-black" data-testid="back-to-pratiche"><ArrowLeft className="h-4 w-4" /> Amministrazione</Link>
      <div className="rp-fade">
        <p className="rp-label">{o.client_name} · {o.code}</p>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-1" data-testid="pratica-title">{o.title}</h1>
        <p className="text-sm text-[#757575] mt-1">{o.delivery_address} · <Link to={`/admin/commesse/${o.id}`} className="underline" data-testid="pratica-open-cantiere">Apri il cantiere</Link></p>
      </div>
      <Section label={`Cliente · ${p.client_progress.done}/${p.client_progress.total}`} testid="section-cliente"
        action={<AssigneeSelect value={p.assignees.cliente} staff={p.staff} onChange={(v) => patch({ assignees: { cliente: v } })} testid="assignee-cliente" />}>
        <p className="text-xs text-[#757575] mb-3">Queste voci compaiono anche nel cantiere del cliente: le richieste e gli invii gli arrivano via email e messaggio, e la voce si chiude da sola quando risponde o firma.</p>
        <div className="space-y-2">{p.client_items.map((it) => <ItemRow key={it.key} it={it} oid={id} scope="cliente" staff={p.staff} onSaved={setP} />)}</div>
      </Section>
      {p.can_subappalti && <Decision p={p} patch={patch} />}
      {p.can_subappalti && p.subcontract_needed !== false && <Parties p={p} kind="subappalto" oid={id} setP={setP} onAdd={() => setParty("subappalto")} onEdit={setEditing} />}
      <Parties p={p} kind="collaboratore" oid={id} setP={setP} onAdd={() => setParty("collaboratore")} onEdit={setEditing} />
      <PartyDialog open={!!party} kind={party} onClose={() => setParty(null)} onSave={addParty} />
      <ExtrasSection p={p} oid={id} setP={setP} />
      <PartyDialog open={!!editing} kind={editing?.kind} initial={editing} onClose={() => setEditing(null)} onSave={editParty} />
      <EmailLog refId={o.id} reload={JSON.stringify(p.client_items.map((i) => i.status))} />
    </div>
  );
}
